import { Edge, MarkerType } from "@xyflow/react"
import {
  WorkflowNode,
  WorkflowEdge,
  TriggerNodeData,
  ConditionNodeData,
  ActionNodeData,
  CHECK_KIND_GROUP,
  CHECK_KIND_LEAF,
  CheckExpression,
  CheckLeaf,
  LeafCheck,
  OPERATOR_AND,
  OPERATOR_OR
} from "lib/types/workflow"
import {
  NodeConditionCheck,
  WorkflowApiNode,
  WorkflowActionDo,
  coerceCheck
} from "lib/types/workflowConditions"
import { v4 as uuidv4 } from "uuid"

const NODE_X_SPACING = 300
const NODE_Y_SPACING = 180

const makeEdge = (
  source: string,
  sourceHandle: string,
  target: string
): WorkflowEdge => ({
  id: `e-${source}-${target}`,
  source,
  sourceHandle,
  target,
  targetHandle: "input",
  markerEnd: { type: MarkerType.ArrowClosed },
  animated: true
})

// ────────────────────────────────────────────────────────────
// Initial state: just a trigger node
// ────────────────────────────────────────────────────────────

export const getInitialNodes = (): {
  nodes: WorkflowNode[]
  edges: WorkflowEdge[]
} => {
  const triggerNode: WorkflowNode = {
    id: "trigger-1",
    type: "trigger",
    position: { x: 250, y: 0 },
    data: {
      nodeType: "TRIGGER",
      label: "Order Created",
      triggerType: "ORDER_CREATED"
    }
  }

  return {
    nodes: [triggerNode],
    edges: []
  }
}

// ────────────────────────────────────────────────────────────
// Expression <-> NodeConditionCheck
// ────────────────────────────────────────────────────────────

/** Lift a leaf to a NodeConditionCheck — leaves already store the canonical
 *  shape, so this is a passthrough. */
export const leafToApiCheck = (leaf: CheckLeaf): NodeConditionCheck =>
  leaf.check

/** Build a NodeConditionCheck from a CheckExpression tree, folding groups
 *  with more than 2 children into a right-leaning chain of AND/OR pairs. */
export const expressionToApiCheck = (expr: CheckExpression): NodeConditionCheck => {
  if (expr.kind === CHECK_KIND_LEAF) return leafToApiCheck(expr)
  const [first, ...rest] = expr.children
  if (!first) {
    // Degenerate empty group — emit a malformed check so the backend rejects
    // it rather than us silently dropping the condition. We cast through
    // unknown because an empty group has no valid wire shape.
    return { type: expr.operator } as unknown as NodeConditionCheck
  }
  if (rest.length === 0) return expressionToApiCheck(first)
  return {
    type: expr.operator,
    check1: expressionToApiCheck(first),
    check2: expressionToApiCheck({
      kind: CHECK_KIND_GROUP,
      operator: expr.operator,
      children: rest
    })
  }
}

/** Inverse of expressionToApiCheck. Same-operator chains on the right spine
 *  are flattened so a serialised AND/OR tree round-trips through the editor
 *  as a single group with N children rather than a deeply nested structure. */
export const apiCheckToExpression = (
  check: NodeConditionCheck
): CheckExpression => {
  if (
    (check.type === OPERATOR_AND || check.type === OPERATOR_OR) &&
    check.check1 &&
    check.check2
  ) {
    const operator = check.type
    const left = apiCheckToExpression(check.check1)
    const right = apiCheckToExpression(check.check2)
    const leftChildren =
      left.kind === CHECK_KIND_GROUP && left.operator === operator
        ? left.children
        : [left]
    const rightChildren =
      right.kind === CHECK_KIND_GROUP && right.operator === operator
        ? right.children
        : [right]
    return {
      kind: CHECK_KIND_GROUP,
      operator,
      children: [...leftChildren, ...rightChildren]
    }
  }
  return { kind: CHECK_KIND_LEAF, check: check as LeafCheck }
}

// ────────────────────────────────────────────────────────────
// Flow → API: serialize canvas nodes to recursive API format
// ────────────────────────────────────────────────────────────

const buildActionDo = (action: ActionNodeData): WorkflowActionDo => {
  const doPayload: WorkflowActionDo = { type: action.actionType }
  if (action.actionType === "CANCELLATION" && action.reason) {
    doPayload.reason = {
      code: action.reason,
      description: action.reasonDescription ?? action.reason
    }
  }
  if (action.actionType === "CUSTOM_STATE" && action.customState) {
    doPayload.customState = action.customState
  }
  if (action.actionType === "DELIVERY") {
    doPayload.selection = {
      giftCardProducts: action.deliveryGiftCardProducts ?? false
    }
  }
  return doPayload
}

const buildApiNode = (
  nodeId: string,
  nodes: WorkflowNode[],
  edges: Edge[],
  visited: Set<string> = new Set()
): WorkflowApiNode | null => {
  if (visited.has(nodeId)) return null
  visited.add(nodeId)

  const node = nodes.find((n) => n.id === nodeId)
  if (!node) return null

  if (node.data.nodeType === "ACTION") {
    const actionData = node.data as ActionNodeData
    const result: WorkflowApiNode = {
      type: "ACTION",
      name: actionData.label,
      do: buildActionDo(actionData),
      then: []
    }

    const childEdge = edges.find((e) => e.source === nodeId)
    if (childEdge) {
      const childNode = buildApiNode(childEdge.target, nodes, edges, visited)
      if (childNode) result.then = [childNode]
    }

    return result
  }

  if (node.data.nodeType === "CONDITION") {
    const conditionData = node.data as ConditionNodeData

    const result: WorkflowApiNode = {
      type: "CONDITION",
      name: conditionData.label,
      check: expressionToApiCheck(conditionData.expression),
      then: [],
      otherwise: []
    }

    const trueEdge = edges.find(
      (e) => e.source === nodeId && e.sourceHandle === "true-output"
    )
    const falseEdge = edges.find(
      (e) => e.source === nodeId && e.sourceHandle === "false-output"
    )

    if (trueEdge) {
      const trueChild = buildApiNode(trueEdge.target, nodes, edges, visited)
      if (trueChild) result.then = [trueChild]
    }
    if (falseEdge) {
      const falseChild = buildApiNode(falseEdge.target, nodes, edges, visited)
      if (falseChild) result.otherwise = [falseChild]
    }

    return result
  }

  return null
}

export const flowToApiJson = (
  nodes: WorkflowNode[],
  edges: Edge[]
): WorkflowApiNode[] => {
  const triggerNode = nodes.find(
    (n) => (n.data as TriggerNodeData).nodeType === "TRIGGER"
  )
  if (!triggerNode) return []

  const triggerEdges = edges.filter((e) => e.source === triggerNode.id)
  const result: WorkflowApiNode[] = []

  for (const edge of triggerEdges) {
    const apiNode = buildApiNode(edge.target, nodes, edges)
    if (apiNode) result.push(apiNode)
  }

  return result
}

// ────────────────────────────────────────────────────────────
// API → Flow: deserialize API nodes into canvas nodes
// ────────────────────────────────────────────────────────────

const processApiNode = (
  apiNode: WorkflowApiNode,
  parentId: string,
  sourceHandle: string,
  x: number,
  y: number,
  nodes: WorkflowNode[],
  edges: WorkflowEdge[]
): void => {
  const nodeId = uuidv4()

  edges.push(makeEdge(parentId, sourceHandle, nodeId))

  if (apiNode.type === "ACTION") {
    const node: WorkflowNode = {
      id: nodeId,
      type: "action",
      position: { x, y },
      data: {
        nodeType: "ACTION",
        label: apiNode.name,
        actionType: apiNode.do?.type ?? "CANCELLATION",
        reason:
          apiNode.do?.type === "CANCELLATION"
            ? apiNode.do?.reason?.code
            : undefined,
        reasonDescription:
          apiNode.do?.type === "CANCELLATION"
            ? apiNode.do?.reason?.description
            : undefined,
        customState:
          apiNode.do?.type === "CUSTOM_STATE"
            ? apiNode.do?.customState
            : undefined,
        deliveryGiftCardProducts:
          apiNode.do?.type === "DELIVERY"
            ? apiNode.do?.selection?.giftCardProducts
            : undefined
      }
    }
    nodes.push(node)

    if (apiNode.then) {
      for (const child of apiNode.then) {
        processApiNode(
          child,
          nodeId,
          "output",
          x,
          y + NODE_Y_SPACING,
          nodes,
          edges
        )
      }
    }
  } else if (apiNode.type === "CONDITION") {
    const check = coerceCheck(apiNode.check)
    const expression: CheckExpression = check
      ? apiCheckToExpression(check)
      : {
          // Unknown / missing check — stub a leaf the user can edit. Cast via
          // unknown because a leaf must hold a real LeafCheck on save.
          kind: CHECK_KIND_LEAF,
          check: { type: "PAYMENT_PROVIDER", fields: {} }
        }

    const data: ConditionNodeData = {
      nodeType: "CONDITION",
      label: apiNode.name,
      expression
    }

    const node: WorkflowNode = {
      id: nodeId,
      type: "condition",
      position: { x, y },
      data
    }
    nodes.push(node)

    if (apiNode.then) {
      for (const child of apiNode.then) {
        processApiNode(
          child,
          nodeId,
          "true-output",
          x - NODE_X_SPACING,
          y + NODE_Y_SPACING,
          nodes,
          edges
        )
      }
    }
    if (apiNode.otherwise) {
      for (const child of apiNode.otherwise) {
        processApiNode(
          child,
          nodeId,
          "false-output",
          x + NODE_X_SPACING,
          y + NODE_Y_SPACING,
          nodes,
          edges
        )
      }
    }
  }
}

export const apiJsonToFlow = (
  apiNodes: WorkflowApiNode[],
  triggerType: string = "ORDER_CREATED"
): { nodes: WorkflowNode[]; edges: WorkflowEdge[] } => {
  const triggerId = "trigger-1"
  const triggerNode: WorkflowNode = {
    id: triggerId,
    type: "trigger",
    position: { x: 250, y: 0 },
    data: {
      nodeType: "TRIGGER",
      label: triggerType === "ORDER_CREATED" ? "Order Created" : triggerType,
      triggerType
    }
  }

  const nodes: WorkflowNode[] = [triggerNode]
  const edges: WorkflowEdge[] = []

  for (let i = 0; i < apiNodes.length; i++) {
    const xOffset = i * NODE_X_SPACING
    processApiNode(
      apiNodes[i],
      triggerId,
      "output",
      250 + xOffset,
      NODE_Y_SPACING,
      nodes,
      edges
    )
  }

  return { nodes, edges }
}
